// Web Speech API wrapper for hands-free dictation.
//
// Privacy: by default Chrome sends the audio to Google's servers for
// recognition. Where the browser offers on-device recognition for the language
// (`processLocally`), we use it and report it, so the clinic can see which
// mode is active.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '@/lib/logger';

const MAX_NETWORK_RETRIES = 5;
const ANDROID = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

type Alternative = { transcript: string; confidence: number };
type Result = { isFinal: boolean; length: number; [index: number]: Alternative };
type ResultEvent = { resultIndex: number; results: { length: number; [index: number]: Result } };
type ErrorEvent = { error: string };

type Recognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  processLocally?: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: ResultEvent) => void) | null;
  onerror: ((e: ErrorEvent) => void) | null;
  onend: (() => void) | null;
};

type Availability = 'available' | 'downloadable' | 'downloading' | 'unavailable';

type RecognitionCtor = {
  new (): Recognition;
  available?: (opts: { langs: string[]; processLocally: boolean }) => Promise<Availability>;
  install?: (opts: { langs: string[]; processLocally: boolean }) => Promise<boolean>;
};

function getCtor(): RecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export type OnDevice = 'yes' | 'downloadable' | 'no' | 'unknown';

/** Recognition errors with their own message (`dictation.error.<code>`). */
const KNOWN_ERRORS = new Set([
  'not-allowed',
  'service-not-allowed',
  'audio-capture',
  'network',
  'language-not-supported',
]);

export function useSpeechRecognition(opts: {
  lang: string;
  onFinal: (text: string, confidence: number) => void;
}) {
  const { t } = useTranslation();
  const ctor = getCtor();
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [onDevice, setOnDevice] = useState<OnDevice>(ctor ? 'unknown' : 'no');

  const recRef = useRef<Recognition | null>(null);
  const wantRef = useRef(false);
  const failuresRef = useRef(0);
  const onFinalRef = useRef(opts.onFinal);
  onFinalRef.current = opts.onFinal;

  useEffect(() => {
    if (!ctor?.available) return;
    ctor
      .available({ langs: [opts.lang], processLocally: true })
      .then((a) =>
        setOnDevice(a === 'available' ? 'yes' : a === 'unavailable' ? 'no' : 'downloadable')
      )
      .catch(() => setOnDevice('no'));
  }, [ctor, opts.lang]);

  const installOnDevice = useCallback(async () => {
    if (!ctor?.install) return;
    try {
      const ok = await ctor.install({ langs: [opts.lang], processLocally: true });
      setOnDevice(ok ? 'yes' : 'no');
    } catch (err) {
      logger.warn('on-device speech install failed', err);
      setOnDevice('no');
    }
  }, [ctor, opts.lang]);

  const startRecognition = useCallback(() => {
    if (!ctor) return;
    const rec = new ctor();
    rec.lang = opts.lang;
    // Android Chrome's continuous mode repeats results as they grow; one
    // utterance per session (restarted on end) is reliable there.
    rec.continuous = !ANDROID;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    if (onDevice === 'yes' && 'processLocally' in rec) rec.processLocally = true;

    rec.onresult = (e) => {
      let live = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const result = e.results[i];
        const alt = result[0];
        if (result.isFinal) {
          const text = alt.transcript.trim();
          failuresRef.current = 0;
          if (text) onFinalRef.current(text, alt.confidence);
        } else {
          live += alt.transcript;
        }
      }
      setInterim(live.trim());
    };
    rec.onerror = (e) => {
      if (e.error === 'no-speech' || e.error === 'aborted') return;
      logger.warn('speech error', e.error);
      setError(
        KNOWN_ERRORS.has(e.error)
          ? t(`dictation.error.${e.error}`)
          : t('dictation.error.other', { code: e.error })
      );
      if (e.error !== 'network') wantRef.current = false;
      // Offline: keep retrying, but slower each time and not for ever.
      failuresRef.current += 1;
      if (failuresRef.current >= MAX_NETWORK_RETRIES) wantRef.current = false;
    };
    rec.onend = () => {
      setInterim('');
      // Chrome ends a session after a pause; keep listening until told to stop.
      if (wantRef.current) {
        const delay = Math.min(failuresRef.current, 4) * 500;
        window.setTimeout(() => {
          if (!wantRef.current) {
            setListening(false);
            recRef.current = null;
            return;
          }
          try {
            rec.start();
          } catch {
            setListening(false);
            recRef.current = null;
          }
        }, delay);
        return;
      }
      setListening(false);
      recRef.current = null;
    };

    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
      setError(null);
    } catch (err) {
      logger.warn('speech start failed', err);
      setError(t('dictation.error.startFailed'));
      wantRef.current = false;
    }
  }, [ctor, onDevice, opts.lang, t]);

  const start = useCallback(() => {
    if (wantRef.current) return;
    wantRef.current = true;
    failuresRef.current = 0;
    startRecognition();
  }, [startRecognition]);

  const stop = useCallback(() => {
    wantRef.current = false;
    recRef.current?.stop();
  }, []);

  useEffect(
    () => () => {
      wantRef.current = false;
      recRef.current?.abort();
    },
    []
  );

  return {
    supported: !!ctor,
    listening,
    interim,
    error,
    clearError: () => setError(null),
    start,
    stop,
    onDevice,
    installOnDevice,
  };
}
